/**
 * System-wide date formatting: dd-mmm-yyyy (e.g. 19-Feb-2025).
 * Use for all date displays; use formatDateTimeDDMMMYYYY when time is also shown.
 */

const MONTHS_MMM = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Format a date as dd-mmm-yyyy (e.g. 19-Feb-2025).
 * @param {Date|string|number} date - Date value
 * @returns {string} Formatted date or '—' if invalid
 */
export function formatDateDDMMMYYYY(date) {
  if (date == null) return '—';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const month = MONTHS_MMM[d.getMonth()];
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
}

/**
 * Format a date with time as dd-mmm-yyyy HH:MM (e.g. 19-Feb-2025 14:30).
 * For UTC dates, pass a Date object; it will be formatted in local time unless you use UTC getters.
 * @param {Date|string|number} date - Date value
 * @param {boolean} useUTC - If true, use UTC components (for server timestamps)
 * @returns {string} Formatted date-time or '—' if invalid
 */
export function formatDateTimeDDMMMYYYY(date, useUTC = false) {
  if (date == null) return '—';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '—';
  const day = String(useUTC ? d.getUTCDate() : d.getDate()).padStart(2, '0');
  const month = MONTHS_MMM[useUTC ? d.getUTCMonth() : d.getMonth()];
  const year = useUTC ? d.getUTCFullYear() : d.getFullYear();
  const h = String(useUTC ? d.getUTCHours() : d.getHours()).padStart(2, '0');
  const m = String(useUTC ? d.getUTCMinutes() : d.getMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} ${h}:${m}`;
}

/**
 * Parse offset string (e.g. "+5:30", "+05:30:00", "-5:00") to minutes from UTC.
 * @param {string|null|undefined} value
 * @returns {number} Minutes (e.g. 330 for +5:30, -300 for -5:00)
 */
export function parseOffsetToMinutes(value) {
  if (value == null || String(value).trim() === '') return 330;
  let s = String(value).trim();
  if (s.endsWith(':00') && s.length >= 6) s = s.slice(0, -3);
  const match = s.match(/^([+-])(\d{1,2}):(\d{2})$/);
  if (!match) return 330;
  const sign = match[1] === '+' ? 1 : -1;
  const hours = parseInt(match[2], 10);
  const mins = parseInt(match[3], 10);
  return sign * (hours * 60 + mins);
}

/**
 * Format "Performed on UTC" as local time (UTC + offset): e.g. UTC 12:48 with offset +5:30 -> "25-Feb-2026 18:18".
 * Used everywhere in the system for Performed on UTC display.
 * @param {Date|string|number|null|undefined} utcDate - UTC timestamp from server
 * @param {string|null|undefined} offsetStr - e.g. "+5:30", "+05:30:00" (default +5:30)
 * @returns {string} Formatted "dd-mmm-yyyy HH:mm" in local (UTC+offset) or '—' if invalid
 */
export function formatPerformedOnUtcToLocal(utcDate, offsetStr) {
  if (utcDate == null) return '—';
  const d = new Date(utcDate);
  if (Number.isNaN(d.getTime())) return '—';
  const offsetMinutes = parseOffsetToMinutes(offsetStr);
  const local = new Date(d.getTime() + offsetMinutes * 60 * 1000);
  const day = String(local.getUTCDate()).padStart(2, '0');
  const month = MONTHS_MMM[local.getUTCMonth()];
  const year = local.getUTCFullYear();
  const h = String(local.getUTCHours()).padStart(2, '0');
  const m = String(local.getUTCMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} ${h}:${m}`;
}

/**
 * Format timezone offset for display: "+05:30:00" or "+05:30" -> "+5:30" (no seconds, no leading zero on hour).
 * Used for Performed At Offset and similar labels everywhere in the system.
 * @param {string|null|undefined} value - Raw offset (e.g. "+05:30:00", "+05:30")
 * @returns {string} Display format "+5:30" or "—" if empty
 */
export function formatOffsetForDisplay(value) {
  if (value == null || String(value).trim() === '') return '—';
  let s = String(value).trim();
  if (s.endsWith(':00') && s.length >= 6) s = s.slice(0, -3);
  const match = s.match(/^([+-])(\d{1,2}):(\d{2})$/);
  if (!match) return s;
  const [, sign, h, m] = match;
  const hour = parseInt(h, 10);
  return `${sign}${hour}:${m}`;
}
